// Immediate, serialized persistence of estimate line items.
//
// ERPNext's PUT replaces the WHOLE child table, so every mutation is applied
// against the freshest server list (fetched right before the PUT), then the
// result is re-fetched and verified. All mutations for a job run through one
// promise queue so rapid successive actions can never overwrite each other.

const queues = {};
export function enqueue(jobId, fn) {
  const prev = queues[jobId] || Promise.resolve();
  const next = prev.then(fn, fn);
  queues[jobId] = next.catch(() => {});
  return next;
}

export const photoUrl = (id) => `https://portal.aaairrigationservice.com/estimates/api/photos/${id}/view`;
export const photoIdFromUrl = (u) => {
  const m = u && u.match(/photos\/([^/]+)\/view/);
  return m ? m[1] : null;
};

export function errMessage(err) {
  const d = err?.response?.data;
  const detail = d?.detail || d?.message || d?._server_messages || d?.exception;
  return (typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : '') || err?.message || 'Unknown error';
}

// Fields we write to ERP (same set the old Save button wrote).
export function toPutRow(r) {
  const qty = Number(r.qty) || 0;
  const rate = Number(r.rate) || 0;
  const out = {
    item_code: r.item_code, item_name: r.item_name, qty, rate, amount: qty * rate,
    description: r.description || '', custom_zone: r.custom_zone || '',
    custom_before_photo_url: r.custom_before_photo_url || '',
    custom_after_photo_url: r.custom_after_photo_url || '',
  };
  if (r.name) out.name = r.name;
  return out;
}

const same = (a, b) =>
  a && b && a.item_code === b.item_code && (a.custom_zone || '') === (b.custom_zone || '') &&
  Math.abs((Number(a.qty) || 0) - (Number(b.qty) || 0)) < 1e-6 &&
  Math.abs((Number(a.rate) || 0) - (Number(b.rate) || 0)) < 1e-6;

export async function fetchServer(api, jobId, stripHtml) {
  const res = await api.getEstimateDetail(jobId);
  const doc = res?.data || {};
  const locked = doc.docstatus === 1 || doc.custom_signature_captured === 1 || doc.custom_approval_status === 'Approved';
  const rows = (doc.items || []).map(i => ({
    name: i.name, item_code: i.item_code, item_name: i.item_name, qty: i.qty, rate: i.rate,
    amount: i.amount, description: stripHtml ? stripHtml(i.description) : (i.description || ''),
    custom_zone: i.custom_zone || '',
    custom_before_photo_url: i.custom_before_photo_url || '',
    custom_after_photo_url: i.custom_after_photo_url || '',
  }));
  return { rows, locked };
}

async function uploadPending(api, jobId, row, lineIdx, hooks) {
  const patch = {};
  for (const type of ['before', 'after']) {
    const dataUrl = row._photos?.[type];
    if (!dataUrl) continue;
    const b64 = dataUrl.split(',')[1];
    const contentType = dataUrl.split(';')[0].replace('data:', '') || 'image/jpeg';
    const slug = (row.item_name || 'item').replace(/[^a-zA-Z0-9]/g, '_');
    const result = await api.uploadPhoto({
      doc_type: 'Quotation', doc_id: jobId, image_base64: b64,
      filename: `zone${row.custom_zone || 'NA'}_${slug}_line${lineIdx}_${type}.${contentType.split('/')[1] || 'jpg'}`,
      content_type: contentType, uploaded_by: hooks.techName || '', line_idx: lineIdx, photo_type: type,
    });
    if (!result?.photo_id) throw new Error('Photo upload returned no id');
    patch[type === 'before' ? 'custom_before_photo_url' : 'custom_after_photo_url'] = photoUrl(result.photo_id);
    hooks.onPhotoDone?.(type, patch);
  }
  return patch;
}

/**
 * Run one mutation. `getRow()` returns the CURRENT local row (latest edits).
 * op: 'add' | 'edit' | 'remove'. Resolves { rows, row } (verified server state)
 * or throws (nothing is dropped by the caller on throw).
 */
export async function runOp({ api, jobId, op, getRow, hooks = {}, stripHtml }) {
  const local = getRow();
  if (!local) return { rows: null, row: null, skipped: true };
  if (op === 'add' && local._removed) return { rows: null, row: null, skipped: true };

  const { rows: server, locked } = await fetchServer(api, jobId, stripHtml);
  if (locked) throw new Error('This estimate is approved/signed and locked — items cannot be changed.');

  let next; let target = null; let priorNames = new Set(server.map(r => r.name));

  if (op === 'add') {
    // Ambiguous earlier attempt (PUT sent, verify/response lost)? Adopt the landed row.
    // The server may auto-sort rows by zone on save, so a landed row is NOT
    // necessarily at the end: identify it by "name not present before the PUT"
    // (_putNames), falling back to the old position-based check for drafts
    // written before this field existed.
    if (local._putNames) {
      const before = new Set(local._putNames);
      const landed = server.filter(r => !before.has(r.name)).find(r => same(r, local));
      if (landed) return { rows: server, row: landed, adopted: true };
    } else if (local._putBase != null) {
      const landed = server.slice(local._putBase).find(r => same(r, local));
      if (landed) return { rows: server, row: landed, adopted: true };
    }
    const patch = await uploadPending(api, jobId, local, server.length + 1, hooks);
    target = { ...local, ...patch };
    next = [...server, target];
    hooks.beforePut?.({ putBase: server.length, putNames: server.map(r => r.name), patch });
  } else {
    const idx = server.findIndex(r => r.name === local.name);
    if (op === 'remove') {
      if (idx < 0) return { rows: server, row: null }; // already gone
      next = server.filter((_, i) => i !== idx);
    } else {
      if (idx < 0) throw new Error('This line no longer exists on the job (changed on another device). Reload.');
      const patch = await uploadPending(api, jobId, local, idx + 1, hooks);
      target = { ...server[idx], ...local, name: local.name, ...patch };
      next = server.map((r, i) => (i === idx ? target : r));
      hooks.beforePut?.({ patch });
    }
  }

  await api.updateEstimate(jobId, { items: next.map(toPutRow) });

  const { rows: check } = await fetchServer(api, jobId, stripHtml);
  if (check.length !== next.length) {
    throw new Error(`Server has ${check.length} of ${next.length} expected items — kept on this device, tap Retry.`);
  }
  let saved = null;
  if (op === 'add') {
    saved = check.filter(r => !priorNames.has(r.name)).find(r => same(r, target)) || null;
    if (!saved) throw new Error('New item was not found on the job after saving — kept on this device, tap Retry.');
  } else if (op === 'edit') {
    saved = check.find(r => r.name === local.name);
    if (!same(saved, target)) throw new Error('Server values do not match what was saved — tap Retry.');
  } else if (check.some(r => r.name === local.name)) {
    throw new Error('Item is still on the job after delete — tap Retry.');
  }
  return { rows: check, row: saved };
}
